/**
 * Fullscreen helpers — extracted from ComradesPage so any primitive can
 * request fullscreen on a player surface across browser quirks.
 *
 * Two-tier strategy:
 *   1. Try the WebKit native-video fullscreen path (iOS Safari's only way
 *      to give the viewer a real full-screen video, including PiP toggle).
 *   2. Fall back to Element.requestFullscreen on the wrapper, which works
 *      everywhere else.
 */

type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
  mozRequestFullScreen?: () => Promise<void> | void
  msRequestFullscreen?: () => Promise<void> | void
}

type FullscreenVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitDisplayingFullscreen?: boolean
  webkitSupportsFullscreen?: boolean
  webkitPresentationMode?: 'inline' | 'picture-in-picture' | 'fullscreen'
  webkitSetPresentationMode?: (mode: 'inline' | 'picture-in-picture' | 'fullscreen') => void
  webkitRequestFullscreen?: () => Promise<void> | void
  mozRequestFullScreen?: () => Promise<void> | void
  msRequestFullscreen?: () => Promise<void> | void
}

function requestElementFullscreen(
  element: FullscreenCapableElement | FullscreenVideoElement | null,
  onReject?: () => void,
): boolean {
  if (!element) return false
  const request =
    element.requestFullscreen?.bind(element) ??
    element.webkitRequestFullscreen?.bind(element) ??
    element.mozRequestFullScreen?.bind(element) ??
    element.msRequestFullscreen?.bind(element)

  if (!request) return false

  try {
    const result = request()
    if (result && typeof (result as Promise<void>).catch === 'function') {
      void (result as Promise<void>).catch(() => onReject?.())
    }
    return true
  } catch {
    onReject?.()
    return false
  }
}

function enterNativeVideoFullscreen(video: FullscreenVideoElement | null): boolean {
  if (!video || video.webkitDisplayingFullscreen || video.webkitPresentationMode === 'fullscreen') {
    return false
  }
  if (video.webkitSupportsFullscreen === false) return false

  try {
    if (video.paused) {
      void video.play().catch(() => undefined)
    }
    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen()
      return true
    }
  } catch {
    // Fall through to setPresentationMode when available.
  }

  try {
    if (video.webkitSetPresentationMode) {
      video.webkitSetPresentationMode('fullscreen')
      return true
    }
  } catch {
    return false
  }

  return false
}

/**
 * Request fullscreen on a player surface element. Prefers the `<video>`
 * inside the surface (iOS Safari requires this), then falls back to the
 * wrapper. Safe to call with null — no-op.
 */
export function requestPlayerFullscreen(
  surface: HTMLElement | null,
  { frameFallback = true }: { frameFallback?: boolean } = {},
): void {
  if (!surface) return

  const video =
    surface instanceof HTMLVideoElement
      ? (surface as FullscreenVideoElement)
      : (surface.querySelector('video') as FullscreenVideoElement | null)

  if (video) {
    if (enterNativeVideoFullscreen(video)) return

    const requested = requestElementFullscreen(video, () => {
      if (enterNativeVideoFullscreen(video)) return
      if (frameFallback) requestElementFullscreen(surface as FullscreenCapableElement)
    })
    if (requested) return
  }

  if (frameFallback) {
    requestElementFullscreen(surface as FullscreenCapableElement)
  }
}

/**
 * Walk up from `start` looking for an element that should be treated as
 * the player surface. Shells mark their player container with
 * `data-fullscreen-root`. Returns null if nothing on the path matches.
 */
export function findPlayerSurface(start: HTMLElement | null): HTMLElement | null {
  if (!start) return null
  return start.closest<HTMLElement>('[data-fullscreen-root]')
}
